import { cloneElement, isValidElement, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { usePhone } from '@/lib/responsive';

/** The name every KPI scroller gets when its strip does not carry its own. */
export const KPI_REGION_LABEL = 'Key metrics';

type StripProps = HTMLAttributes<HTMLElement>;

/**
 * The keyboard contract of a KPI strip that is its own scroll container
 * (`.kpi-strip` is `overflow-x: auto` above the phone breakpoint): a focusable,
 * named region, so a strip wider than its pane scrolls by keyboard too (axe
 * scrollable-region-focusable, the 2026-10 route crawl at 1440 px). A strip's
 * own `aria-label` is kept.
 */
export function kpiScrollRegionProps(label?: string): Pick<StripProps, 'role' | 'tabIndex' | 'aria-label'> {
  return { role: 'region', tabIndex: 0, 'aria-label': label ?? KPI_REGION_LABEL };
}

/**
 * On phones the KPI strip scrolls sideways with snap points, showing a little
 * over two tiles so the peek is the affordance. Every metric stays one swipe
 * away and no toggle row is spent on it; the section is the CSS hook.
 *
 * Exactly one scroller is focusable at any width: on phones the section
 * scrolls and the strip inside it is `overflow: visible`; elsewhere the strip
 * itself scrolls, so it takes the region contract instead.
 */
export function ResponsiveKpis({ children }: { children: ReactNode }) {
  const phone = usePhone();
  if (!phone) {
    if (!isValidElement<StripProps>(children)) return <>{children}</>;
    const strip: ReactElement<StripProps> = children;
    return cloneElement(strip, kpiScrollRegionProps(strip.props['aria-label']));
  }
  // The section is the scroller, focusable so the strip scrolls by keyboard too.
  return (
    <section className="mobile-kpi-block" aria-label={KPI_REGION_LABEL} role="region" tabIndex={0}>
      {children}
    </section>
  );
}
